import { useCallback, useMemo, useState, type RefObject } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  OrthoApprovalStatus,
  TaskStatus,
  type AnalyzeSnipsRequest,
} from "api/model";
import {
  getGetOrthosQueryKey,
  useApproveOrtho,
  useRejectOrtho,
  useRestartOrthoScaleodmTask,
  useSetOrthoVisibility,
  useStartOrthoCreation,
} from "api/orthos/orthos";
import { getGetRowSnipsQueryKey } from "api/snips/snips";
import {
  getGetAllTasksQueryKey,
  useCreateAnalyzeCanopyCoverTask,
  useCreateAnalyzeEmergenceTask,
} from "api/tasks/tasks";
import { useCogOrtho } from "context/CogOrtho";

import type {
  AnalyzeStepName,
  OrthoWorkflow,
  OrthoWorkflowKey,
  StartingPlotTasks,
  StartingPlotTasksByKey,
  WorkflowStepName,
} from "./orthoWorkflow";

export const ORTHO_WORKFLOW_ACTION = {
  approve: "approve",
  reject: "reject",
  retryOrthoCreation: "retryOrthoCreation",
  startOrthoCreation: "startOrthoCreation",
  startAnalyzeEmergence: "startAnalyzeEmergence",
  startAnalyzeCanopy: "startAnalyzeCanopy",
  setVisibleOnMap: "setVisibleOnMap",
} as const;

export type OrthoWorkflowActionName =
  (typeof ORTHO_WORKFLOW_ACTION)[keyof typeof ORTHO_WORKFLOW_ACTION];

/** Acts on one workflow, identified by key. Failures surface as workflow errors. */
export interface OrthoWorkflowActions {
  approve: (key: OrthoWorkflowKey) => void;
  reject: (key: OrthoWorkflowKey) => void;
  setVisibleOnMap: (key: OrthoWorkflowKey, visibleOnMap: boolean) => void;
  /** Restarts a failed ortho_creation task. */
  retryOrthoCreation: (key: OrthoWorkflowKey) => void;
  /** Starts ortho creation from the images uploaded so far, e.g. when some failed and it wasn't started automatically. */
  startOrthoCreation: (key: OrthoWorkflowKey) => void;
  /** Analyzes emergence in the approved ortho's plots for `trialId`; also re-runs a failed or partial analysis. */
  startAnalyzeEmergence: (key: OrthoWorkflowKey, trialId: number) => void;
  /** Analyzes canopy cover in the approved ortho's plots for `trialId`; also re-runs a failed or partial analysis. */
  startAnalyzeCanopy: (key: OrthoWorkflowKey, trialId: number) => void;
}

/** The `detail` of an error response: FastAPI sends a string, validation errors a list. */
const getResponseErrorDetail = (data: unknown): string | null => {
  if (data == null || typeof data !== "object" || !("detail" in data)) {
    return null;
  }
  const { detail } = data;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail.flatMap((item: unknown) =>
      item != null &&
      typeof item === "object" &&
      "msg" in item &&
      typeof item.msg === "string"
        ? [item.msg]
        : [],
    );
    return messages.length > 0 ? messages.join("; ") : null;
  }
  return null;
};

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

interface UseOrthoWorkflowActionsParams {
  /**
   * The latest workflows. Actions read them through a ref so they stay
   * referentially stable while workflows change on every poll.
   */
  byKeyRef: RefObject<ReadonlyMap<OrthoWorkflowKey, OrthoWorkflow>>;
  reportError: (
    key: OrthoWorkflowKey,
    error: { step: WorkflowStepName; message: string; trialId?: number },
  ) => void;
  clearError: (
    key: OrthoWorkflowKey,
    step: WorkflowStepName,
    trialId?: number,
  ) => void;
}

interface PendingAction {
  key: OrthoWorkflowKey;
  /** The trial an analysis is being started for. */
  trialId?: number;
}

type PendingByAction = Record<OrthoWorkflowActionName, PendingAction[]>;

const NOTHING_PENDING: PendingByAction = {
  [ORTHO_WORKFLOW_ACTION.approve]: [],
  [ORTHO_WORKFLOW_ACTION.reject]: [],
  [ORTHO_WORKFLOW_ACTION.retryOrthoCreation]: [],
  [ORTHO_WORKFLOW_ACTION.startOrthoCreation]: [],
  [ORTHO_WORKFLOW_ACTION.startAnalyzeEmergence]: [],
  [ORTHO_WORKFLOW_ACTION.startAnalyzeCanopy]: [],
  [ORTHO_WORKFLOW_ACTION.setVisibleOnMap]: [],
};

/**
 * Mutations on individual workflows. Pending state is tracked per workflow key
 * (not per mutation observer), so several workflows can act at once and each
 * item still shows its own spinner. Errors go to the provider's per-workflow
 * error store so they outlive the item that triggered them.
 */
export const useOrthoWorkflowActions = ({
  byKeyRef,
  reportError,
  clearError,
}: UseOrthoWorkflowActionsParams): {
  actions: OrthoWorkflowActions;
  isActionPending: (
    action: OrthoWorkflowActionName,
    key: OrthoWorkflowKey,
  ) => boolean;
  /** Analyze starts in flight, for `buildOrthoWorkflows`. */
  startingPlotTasks: StartingPlotTasksByKey;
} => {
  const { displayOrtho } = useCogOrtho();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingByAction>(NOTHING_PENDING);

  const { mutateAsync: approveOrtho } = useApproveOrtho();
  const { mutateAsync: rejectOrtho } = useRejectOrtho();
  const { mutateAsync: setOrthoVisibility } = useSetOrthoVisibility();
  const { mutateAsync: restartOrthoCreation } = useRestartOrthoScaleodmTask();
  const { mutateAsync: startOrthoCreation } = useStartOrthoCreation();
  const { mutateAsync: startAnalyzeEmergence } =
    useCreateAnalyzeEmergenceTask();
  const { mutateAsync: startAnalyzeCanopy } = useCreateAnalyzeCanopyCoverTask();

  /**
   * Runs `request` for one workflow: marks it pending, clears the step's old
   * error, reports a non-200 response or thrown error against the step, and
   * calls `onSuccess` otherwise.
   */
  const perform = useCallback(
    async <Response extends { status: number; data: unknown }>({
      action,
      key,
      trialId,
      step,
      failureMessage,
      request,
      onSuccess,
    }: {
      action: OrthoWorkflowActionName;
      key: OrthoWorkflowKey;
      trialId?: number;
      step: WorkflowStepName;
      failureMessage: string;
      request: () => Promise<Response>;
      onSuccess: () => void;
    }) => {
      clearError(key, step, trialId);
      const pendingAction: PendingAction = { key, trialId };
      setPending((current) => ({
        ...current,
        [action]: [...current[action], pendingAction],
      }));
      try {
        const response = await request();
        if (response.status !== 200) {
          const detail = getResponseErrorDetail(response.data);
          reportError(key, {
            message:
              detail != null
                ? `${failureMessage}: ${detail}`
                : `${failureMessage}. Please try again.`,
            step,
            trialId,
          });
          return;
        }
        onSuccess();
      } catch (error) {
        reportError(key, {
          message: getErrorMessage(
            error,
            `${failureMessage}. Please try again.`,
          ),
          step,
          trialId,
        });
      } finally {
        setPending((current) => ({
          ...current,
          [action]: current[action].filter((entry) => entry !== pendingAction),
        }));
      }
    },
    [clearError, reportError],
  );

  const actions = useMemo<OrthoWorkflowActions>(() => {
    const invalidateOrthos = () =>
      void queryClient.invalidateQueries({ queryKey: getGetOrthosQueryKey() });
    const invalidateTasks = () =>
      void queryClient.invalidateQueries({
        queryKey: getGetAllTasksQueryKey(),
      });

    const startAnalyze = (
      key: OrthoWorkflowKey,
      trialId: number,
      action: OrthoWorkflowActionName,
      step: AnalyzeStepName,
      failureMessage: string,
      request: (
        data: AnalyzeSnipsRequest,
      ) => ReturnType<typeof startAnalyzeEmergence>,
    ) => {
      const ortho = byKeyRef.current.get(key)?.ortho;
      if (ortho?.status !== OrthoApprovalStatus.approved) return;
      void perform({
        action,
        failureMessage,
        key,
        trialId,
        onSuccess: () => {
          invalidateTasks();
          void queryClient.invalidateQueries({
            queryKey: getGetRowSnipsQueryKey({
              ortho_id: ortho.id,
              trial_id: trialId,
            }),
          });
        },
        request: () => request({ ortho_id: ortho.id, trial_id: trialId }),
        step,
      });
    };

    return {
      approve: (key) => {
        const ortho = byKeyRef.current.get(key)?.ortho;
        if (ortho == null) return;
        void perform({
          action: ORTHO_WORKFLOW_ACTION.approve,
          failureMessage: "Failed to approve ortho",
          key,
          onSuccess: invalidateOrthos,
          request: () => approveOrtho({ orthoId: ortho.id }),
          step: "review",
        });
      },
      reject: (key) => {
        const ortho = byKeyRef.current.get(key)?.ortho;
        if (ortho == null) return;
        void perform({
          action: ORTHO_WORKFLOW_ACTION.reject,
          failureMessage: "Failed to reject ortho",
          key,
          onSuccess: invalidateOrthos,
          request: () => rejectOrtho({ orthoId: ortho.id }),
          step: "review",
        });
      },
      setVisibleOnMap: (key, visibleOnMap) => {
        const ortho = byKeyRef.current.get(key)?.ortho;
        if (ortho == null) return;
        const shouldClearDisplayed = ortho.isVisible && !visibleOnMap;
        void perform({
          action: ORTHO_WORKFLOW_ACTION.setVisibleOnMap,
          failureMessage: "Failed to update ortho visibility",
          key,
          onSuccess: () => {
            invalidateOrthos();
            if (shouldClearDisplayed) {
              displayOrtho(null);
            }
          },
          request: () =>
            setOrthoVisibility({
              data: { visible_on_map: visibleOnMap },
              orthoId: ortho.id,
            }),
          step: "review",
        });
      },
      retryOrthoCreation: (key) => {
        const task = byKeyRef.current.get(key)?.orthoCreation.task;
        if (task?.status !== TaskStatus.failed) return;
        void perform({
          action: ORTHO_WORKFLOW_ACTION.retryOrthoCreation,
          failureMessage: "Failed to retry ortho creation",
          key,
          onSuccess: invalidateTasks,
          request: () => restartOrthoCreation({ taskId: task.id }),
          step: "ortho_creation",
        });
      },
      startOrthoCreation: (key) => {
        const workflow = byKeyRef.current.get(key);
        const ortho = workflow?.ortho;
        if (
          workflow == null ||
          ortho == null ||
          ortho.status !== OrthoApprovalStatus.uploading ||
          workflow.orthoCreation.task != null
        ) {
          return;
        }
        void perform({
          action: ORTHO_WORKFLOW_ACTION.startOrthoCreation,
          failureMessage: "Failed to start ortho creation",
          key,
          onSuccess: () => {
            // Start moves the ortho from uploading to processing.
            invalidateOrthos();
            invalidateTasks();
          },
          request: () =>
            startOrthoCreation({
              data: { ortho_id: ortho.id },
            }),
          step: "ortho_creation",
        });
      },
      startAnalyzeEmergence: (key, trialId) =>
        startAnalyze(
          key,
          trialId,
          ORTHO_WORKFLOW_ACTION.startAnalyzeEmergence,
          "analyze_emergence",
          "Failed to start emergence analysis",
          (data) => startAnalyzeEmergence({ data }),
        ),
      startAnalyzeCanopy: (key, trialId) =>
        startAnalyze(
          key,
          trialId,
          ORTHO_WORKFLOW_ACTION.startAnalyzeCanopy,
          "analyze_canopy",
          "Failed to start canopy analysis",
          (data) => startAnalyzeCanopy({ data }),
        ),
    };
  }, [
    byKeyRef,
    startOrthoCreation,
    approveOrtho,
    displayOrtho,
    perform,
    queryClient,
    rejectOrtho,
    restartOrthoCreation,
    setOrthoVisibility,
    startAnalyzeCanopy,
    startAnalyzeEmergence,
  ]);

  const isActionPending = useCallback(
    (action: OrthoWorkflowActionName, key: OrthoWorkflowKey) =>
      pending[action].some((entry) => entry.key === key),
    [pending],
  );

  const startingPlotTasks = useMemo(() => {
    const byKey: Partial<
      Record<OrthoWorkflowKey, { [Step in keyof StartingPlotTasks]: number[] }>
    > = {};
    const add = (
      entries: readonly PendingAction[],
      step: keyof StartingPlotTasks,
    ) => {
      for (const { key, trialId } of entries) {
        if (trialId != null) {
          (byKey[key] ??= {
            analyzeEmergence: [],
            analyzeCanopy: [],
          })[step].push(trialId);
        }
      }
    };
    add(
      pending[ORTHO_WORKFLOW_ACTION.startAnalyzeEmergence],
      "analyzeEmergence",
    );
    add(pending[ORTHO_WORKFLOW_ACTION.startAnalyzeCanopy], "analyzeCanopy");
    return byKey;
  }, [pending]);

  return { actions, isActionPending, startingPlotTasks };
};
